import { State } from '@progress/kendo-data-query';
import { KeysetPagingResultMeta } from './keyset-paging-meta';

/**
 * Per-grid-instance keyset pager state: virtual skip, cursors, and anchor de-dupe.
 */
export class KeysetPagingSession
{
  public hasMore = false;
  public hasPrevious = false;

  private lastCommittedAnchor: string | null = null;
  private issuedForAnchor: string | null = null;
  private virtualSkip = 0;
  private lastOutgoingPageSize: number | null = null;
  private lastRequestTake: number | null = null;

  private cursorNext: any | null = null;
  private cursorPrev: any | null = null;

  public get virtualSkipValue(): number
  {
    return this.virtualSkip;
  }

  public get nextCursor(): any | null
  {
    return this.cursorNext;
  }

  public get prevCursor(): any | null
  {
    return this.cursorPrev;
  }

  public get committedAnchor(): string | null
  {
    return this.lastCommittedAnchor;
  }

  /** Stable fingerprint for filter/sort/view context — exclude skip/take. */
  public computeAnchorFingerprint(state: Pick<State, 'sort' | 'filter'>, extras: Record<string, any>): string
  {
    return JSON.stringify({
      ...extras,
      sort: state.sort,
      filter: state.filter
    });
  }

  /** If true, caller should sync {@code state.skip} to {@link virtualSkipValue} and skip HTTP. */
  public shouldUseCachedFirstPageWithoutRefetch(currentAnchor: string, force: boolean): boolean
  {
    return !force && currentAnchor === this.lastCommittedAnchor && this.issuedForAnchor === currentAnchor;
  }

  /** Call immediately before issuing a first-page keyset POST for this anchor. */
  public commitAnchorForIssuedFetch(currentAnchor: string): void
  {
    this.lastCommittedAnchor = currentAnchor;
    this.issuedForAnchor = currentAnchor;
  }

  /** Clears cursors and pager flags when filter/sort/view context changes (before a new first-page fetch). */
  public resetNavigationState(): void
  {
    this.virtualSkip = 0;
    this.hasMore = false;
    this.hasPrevious = false;
    this.cursorNext = null;
    this.cursorPrev = null;
  }

  /** True when {@code state.take} changed since the last keyset response (page size change). */
  public hasPageSizeChanged(stateTake: number): boolean
  {
    return this.lastRequestTake !== null && stateTake !== this.lastRequestTake;
  }

  /** Updates virtual skip and cursors from server metadata. */
  public applyServerResult(
    paging: KeysetPagingResultMeta,
    rowTotal: number,
    dataLength: number
  ): void
  {
    const prevOutgoing = this.lastOutgoingPageSize;
    if (paging.requestMode === 'NAV_LAST')
    {
      this.virtualSkip = Math.max(0, rowTotal - dataLength);
    }
    else if (paging.requestNavigationDirection === 'NEXT' && !paging.hadRequestCursor)
    {
      this.virtualSkip = 0;
    }
    else if (paging.requestNavigationDirection === 'NEXT')
    {
      this.virtualSkip += (prevOutgoing ?? paging.requestSize);
    }
    else if (paging.requestNavigationDirection === 'PREV')
    {
      this.virtualSkip = Math.max(0, this.virtualSkip - paging.requestSize);
    }
    this.lastOutgoingPageSize = paging.requestSize;
    this.lastRequestTake = paging.requestSize;
    this.hasMore = paging.hasMore;
    this.hasPrevious = paging.hasPrevious;
    this.cursorNext = paging.nextCursor ?? null;
    this.cursorPrev = paging.previousCursor ?? null;
  }

  public displayTotal(dataLength: number): number
  {
    return this.virtualSkip + dataLength + (this.hasMore ? 1 : 0);
  }
}
